import dayjs, { ConfigType } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { WaitlistInvitationStatus } from './components/waitlist-invitation-page-component';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

export const getDatesDiffInMinutes = (startDateStr?: string, endDateStr?: string): number => {
  if (!startDateStr || !endDateStr) {
    return 0;
  }

  const startDate = dayjs(startDateStr);
  const endDate = dayjs(endDateStr);

  if (!startDate.isValid() || !endDate.isValid()) {
    return 0;
  }

  return endDate.diff(startDate, 'minute');
};

// Utility function to get today's date in a specific timezone
export const getTodaysDateInTimezone = (locationTimezone?: string, format?: string): string => {
  const dateFormat = format || 'YYYY-MM-DD';
  if (locationTimezone) {
    return dayjs().tz(locationTimezone).format(dateFormat);
  }
  return dayjs().format(dateFormat);
};

// Utility function to get minimum time rounded to next 15-minute interval
export const getMinTimeRoundedToNext15Minutes = (locationTimezone?: string): string => {
  const now = locationTimezone ? dayjs().tz(locationTimezone) : dayjs();
  const minutes = now.minute();
  const roundedMinutes = Math.ceil((minutes + 1) / 15) * 15; // Add 1 minute buffer and round to next 15 min
  const nextTime = now.minute(0).second(0).millisecond(0).add(roundedMinutes, 'minute');

  // If we've gone past the current hour, the add operation will handle it correctly
  return nextTime.format('HH:mm');
};

export const getRelativeTime = (dateStr?: string): string => {
  if (!dateStr) return '';

  return dayjs(dateStr).fromNow();
};

export const formatHistoryTableDate = (dateStr: string): string => {
  const date = dayjs(dateStr);
  const today = dayjs();

  if (date.isSame(today, 'day')) {
    return `Today ${date.format('h:mm A')}`;
  } else if (date.isSame(today.subtract(1, 'day'), 'day')) {
    return `Yesterday ${date.format('h:mm A')}`;
  } else {
    return date.format('MMM D, h:mm A');
  }
};

export const isValidDate = (date: dayjs.Dayjs): boolean => {
  // Check if the date is valid and not before the year 1900
  return date.isValid() && date.year() >= 1900;
};

// Define an interface for the input options
export interface FormatDateTimeOptions {
  fromTimezone?: string; // The source timezone (e.g., 'America/New_York')
  toTimezone?: string; // The target timezone (e.g., 'Asia/Tokyo')
  inputFormat?: string; // The format of the input date-time (optional)
  outputFormat?: string; // The desired output format (optional)
}

// TODO: Move this to a common place to be used for both Schedule and Quick Fill
export function formatDateTime(inputDateTime: ConfigType, options?: FormatDateTimeOptions): string {
  const { fromTimezone, toTimezone, inputFormat, outputFormat = 'MM/DD/YYYY hh:mm A' } = options || {};

  if (!inputDateTime) {
    return '';
  }

  // remove Z from the input date-time string to avoid parsing issues (as for that case, it's already in UTC)
  if (typeof inputDateTime === 'string' && inputDateTime.endsWith('Z')) {
    inputDateTime = inputDateTime.slice(0, -1);
  }

  const date = inputFormat ? dayjs(inputDateTime, inputFormat) : dayjs(inputDateTime);

  if (!isValidDate(date)) {
    return '';
  }

  // Assuming we no need to convert to timezone if either of from or to timezone is not provided
  if (!fromTimezone || !toTimezone) {
    return date.format(outputFormat);
  }

  const convertedDate = date.tz(fromTimezone, true).tz(toTimezone);

  return convertedDate.format(outputFormat);
}

// TODO: Move this to a common place to be used for both Schedule and Quick Fill
// Returns input label with timezone abbreviation
export const getLabelWithTimezoneAbbr = (label: string, timezoneAbbr: string) => {
  if (timezoneAbbr) {
    return `${label} ${timezoneAbbr ? `(${timezoneAbbr})` : ''}`.trim();
  }
  return label;
};

export const getInvitationStatusValue = (
  status?: SchedulerV3.Types.GetWaitlistInvitationDetailsIO['output']['waitlistInvitationStatus']
): WaitlistInvitationStatus => {
  switch (status) {
    case 'PENDING':
      return 'invite-pending';
    case 'BOOKED':
      return 'invite-accepted';
    case 'REJECTED':
      return 'invite-declined';
    case 'SLOT_UNAVAILABLE':
    default:
      return 'appointment-unavailable';
  }
};
